<template>
  <div>
    <AuthorView
      :publications="publications"
      :searcher="searcher"
      :sort-strategy="sortStrategy"
      :custom-counter="customCounter"
      :author="author"
    />
    <AppFooter />
  </div>
</template>

<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('index.vue');
import pageCommonMixin from '@/components/mixins/pageCommonMixin';
import publicationUtils from '@/services/utils/publicationUtils';

import CustomCategoriesEnum from '@shared/enums/CustomCategoriesEnum';
import ManagePublicationsStates from '@/enums/ManagePublicationsStatesEnum';
import SearchPublicationsFactory from '@/classes/factories/SearchPublicationsFactory';

import AuthorView from '@/components/views/CategoryView/AuthorView.vue';
import AppFooter from '@/components/views/AppFooter/AppFooter.vue';

function getSearcher(store, author) {
  const currentLanguage = store.getters['ContextStore/currentLanguageGetter'];
  const authorSearcher = store.getters['LibraryStore/getAuthorSearcher'];

  const newSearcher = SearchPublicationsFactory.searcherToBuilder(
    authorSearcher
  )
    .setState(ManagePublicationsStates.LIBRARY)
    .setLanguage(currentLanguage)
    .setAuthorSlug(author)
    .setCollectionIncluded(true)
    .setExcludedCategories([
      CustomCategoriesEnum.NEW,
      CustomCategoriesEnum.RECENT,
      CustomCategoriesEnum.SUGGESTED_BOOK
    ])
    .build();
  return newSearcher;
}

async function libraryState(store) {
  if (process.server) {
    store.dispatch('LibraryStore/destroyStates', [
      ManagePublicationsStates.LIBRARY
    ]);
  }
  await store.dispatch('LibraryStore/initState', {
    managePublicationsState: ManagePublicationsStates.LIBRARY
  });
}

let authorDataLoaded = false;

async function loadAuthorData(store, authorSlug, authorName) {
  let author = await store.dispatch('LibraryStore/getAuthorBySlug', {
    slug: authorSlug
  });
  if (author?._id) {
    author.imageUrl = await store.dispatch(
      'LibraryStore/getAuthorImageUrl',
      author?.image
    );
  } else {
    author = {
      name: authorName
    };
  }
  return author;
}

export default {
  name: 'AuthorPage',
  components: {
    AuthorView,
    AppFooter
  },
  mixins: [pageCommonMixin],
  async asyncData({ store, params }) {
    try {
      await libraryState(store);
      const searcher = getSearcher(store, params.authorSlug);
      let publications = store.getters['LibraryStore/searchPublications'](
        searcher
      );
      let customCounter;
      const sortStrategy = store.getters['ContextStore/librarySortStrategy'];
      if (process.server && publications?.length) {
        customCounter = publicationUtils.countPublicationItems(publications);
        publications = publicationUtils.sortPublication(
          publications,
          sortStrategy
        );
        publications = publications.slice(0, 15);
        store.commit('LibraryStore/destroyLibrary');
      }
      const { meta, title } = await store.dispatch(
        'ContextStore/getAuthorPageHeadInfo',
        params.authorSlug
      );
      const authorName = publications?.[0]?.author || params.author || '';
      let author = await loadAuthorData(store, params.authorSlug, authorName);
      authorDataLoaded = true;

      return {
        publications,
        searcher,
        sortStrategy,
        author,
        authorName,
        customCounter,
        meta,
        title
      };
    } catch (error) {
      logger.fatal(`Get error on create category page error: ${error}`);
    }
    return {};
  },
  data() {
    return {
      searcher: this.searcher,
      publications: this.publications,
      sortStrategy: this.sortStrategy
    };
  },
  head() {
    const headSettings = this.getCommonHeadSettings();

    return {
      ...headSettings,
      link: this.link,
      title: this.title,
      meta: this.meta
    };
  },
  async mounted() {
    this.$store.subscribe(mutation => {
      if (mutation.type === 'ContextStore/setServiceClientInit') {
        this.publications = null;
        this.customCounter = null;
      }
    });
    if (!authorDataLoaded) {
      this.author = await loadAuthorData(
        this.$store,
        this.$route.params.authorSlug,
        this.authorName
      );
    }
  }
};
</script>

<style></style>
